import { useState } from "react";
import { useMutation } from "@apollo/client";
import { useForm } from "react-hook-form";
import { Navigate } from "react-router-dom";

import Button from "@/components/Button";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { UPSERT_CAREER_INTENT_MUTATION } from "../mutations";
import CareerGoalsTab from "./_CareerGoalsTab";
import CareerGoalsTabSkeleton from "./_CareerGoalsTabSkeleton";
import CareerGoalsWalkthrough from "./_CareerGoalsWalkthrough";
import CareerProfileFocusedChrome from "./_CareerProfileFocusedChrome";
import { useCareerProfileQuery } from "./_useCareerProfileQuery";
import {
  buildUpsertCareerIntentInput,
  defaultCareerIntentFormValues,
  hydrateCareerIntentForm,
  type CareerIntentFormValues
} from "./careerIntentForm";

export default function CareerProfileGoalsPage() {
  const {
    pgaId,
    flagsLoaded,
    capabilitiesLoaded,
    employmentRedirect,
    loading,
    error,
    currentGoal,
    memberClass,
    canManageCareerPlan,
    refetch
  } = useCareerProfileQuery();
  const canEdit = Boolean(canManageCareerPlan);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const goals = currentGoal ? [currentGoal] : [];

  const form = useForm<CareerIntentFormValues>({
    defaultValues: defaultCareerIntentFormValues()
  });

  const [upsertCareerIntent] = useMutation(UPSERT_CAREER_INTENT_MUTATION);

  const startEditing = () => {
    form.reset(hydrateCareerIntentForm(currentGoal));
    setIsEditing(true);
  };

  const cancelEditing = () => {
    form.reset(hydrateCareerIntentForm(currentGoal));
    setIsEditing(false);
  };

  const saveGoals = async (values: CareerIntentFormValues) => {
    if (!pgaId) return;

    setIsSubmitting(true);
    try {
      const { data: mutationData, errors } = await upsertCareerIntent({
        variables: { input: buildUpsertCareerIntentInput(pgaId, values) }
      });

      if (errors?.length) {
        showErrorSnackbar(errors[0]?.message || "Failed to save Check-In");
        return;
      }

      const payload = mutationData?.upsertCareerIntent;
      if (!payload?.success) {
        showErrorSnackbar(payload?.message || "Failed to save Check-In");
        return;
      }

      showSnackbar("Check-In saved");
      setIsEditing(false);
      void refetch();
    } catch (saveError) {
      showErrorSnackbar(saveError instanceof Error ? saveError.message : "Failed to save Check-In");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!flagsLoaded || !capabilitiesLoaded) return null;
  if (employmentRedirect) return <Navigate to={employmentRedirect} replace />;
  if (!pgaId) return null;

  const showCreate = canEdit && !isEditing && goals.length === 0;
  const showEdit = canEdit && !isEditing && goals.length > 0;

  if (isEditing) {
    return (
      <CareerGoalsWalkthrough
        currentRole={memberClass}
        form={form}
        isSubmitting={isSubmitting}
        pgaId={pgaId}
        onCancel={cancelEditing}
        onSubmit={saveGoals}
      />
    );
  }

  return (
    <CareerProfileFocusedChrome
      pgaId={pgaId}
      actions={
        showCreate ? (
          <Button onClick={startEditing} testId="career-goals-create">
            Create Check-In
          </Button>
        ) : null
      }
      activeTab="goals"
    >
      {loading ? (
        <CareerGoalsTabSkeleton />
      ) : error ? (
        <div className="mt-12">Error: {error.message}</div>
      ) : (
        <CareerGoalsTab
          goals={goals}
          isLatest
          onCreate={showCreate ? startEditing : undefined}
          onEdit={showEdit ? startEditing : undefined}
        />
      )}
    </CareerProfileFocusedChrome>
  );
}
